function isRejectedAction(action) {
  return action.type.endsWith("rejected")
}

function isFulfilledAction(action) {
  return action.type.endsWith("fulfilled")
}

function isPendingAction(action) {
  return action.type.endsWith("pending")
}

function getHeaders(headersData) {
  const headers = ["x-total-count"]
  return headers.reduce((obj, val) => {
    obj[val] = headersData[val]
    return obj
  }, {})
}

// Used when callSettledAPI is used to make multiple API calls and we need to parse the response
function parseCustomPromiseResponse(arr) {
  let resList = []
  if (arr?.length > 0) {
    resList = arr.map((item) => {
      if (item.status === "fulfilled") return item.value
      else return null
    })
  }
  return resList
}
// Used in extraReducers in slices. This function matches the asyncThunk actions to their handlers
// This allows us to use the same object pattern in all our microfrontends, and not have to write builders every time
export const asyncAPIReducers =
  (
    reducersObj: Record<
      string,
      {
        success: (state, action) => void
        pending?: (state, action) => void
        rejected?: (state, action) => void
      }
    >
  ) =>
  (builder) => {
    // RTK best practice: Use addMatcher for async thunk pending states
    builder.addMatcher(
      (action) => isPendingAction(action),
      (state, action) => {
        const type = action.type.split("/")[0]
        if (reducersObj[type]?.pending) {
          reducersObj[type].pending(state, action)
        }
      }
    )

    // RTK best practice: Use addMatcher for async thunk fulfilled states
    builder.addMatcher(
      (action) => isFulfilledAction(action),
      (state, action) => {
        // Transform payload for headers and custom promises (backwards compatibility)
        let transformedAction = { ...action }

        if (action.payload?.headers) {
          transformedAction = {
            ...action,
            meta: {
              ...action.meta,
              headers: getHeaders(action.payload.headers)
            },
            payload: action.payload.data
          }
        }

        if (action.payload?.custom) {
          transformedAction = {
            ...action,
            payload: parseCustomPromiseResponse(action.payload.promise)
          }
        }

        const type = action.type.split("/")[0]
        if (reducersObj[type]?.success) {
          reducersObj[type].success(state, transformedAction)
        }
      }
    )

    // RTK best practice: Use addMatcher for async thunk rejected states
    builder.addMatcher(
      (action) => isRejectedAction(action),
      (state, action) => {
        const type = action.type.split("/")[0]
        if (reducersObj[type]?.rejected) {
          reducersObj[type].rejected(state, action)
        }
      }
    )

    // Backwards compatibility: Handle regular actions (non-async thunk actions)
    builder.addDefaultCase((state, action) => {
      // Skip if this is an async thunk action (already handled by matchers above)
      if (
        isPendingAction(action) ||
        isFulfilledAction(action) ||
        isRejectedAction(action)
      ) {
        return
      }

      // Handle legacy regular actions
      const type = action.type.split("/")[0]

      // Transform payload for backwards compatibility
      let transformedAction = { ...action }

      if (action.payload?.headers) {
        transformedAction = {
          ...action,
          meta: {
            ...action.meta,
            headers: getHeaders(action.payload.headers)
          },
          payload: action.payload.data
        }
      }

      if (action.payload?.custom) {
        transformedAction = {
          ...action,
          payload: parseCustomPromiseResponse(action.payload.promise)
        }
      }

      // For regular actions, typically only success handler exists
      if (reducersObj[type]?.success) {
        reducersObj[type].success(state, transformedAction)
      }
    })
  }
